import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MapPin, Package, Search } from "lucide-react";

import { useSearch } from "@/api/queries";
import type { SearchHit } from "@/api/types";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/cn";

const GROUPS = [
  { key: "products", label: "Produits", icon: Package },
  { key: "locations", label: "Emplacements", icon: MapPin },
  { key: "containers", label: "Contenants", icon: Box },
] as const;

function hrefFor(hit: SearchHit): string {
  switch (hit.kind) {
    case "product":
      return `/products/${hit.id}`;
    case "location":
      return `/locations/${hit.id}`;
    case "container":
      return `/containers/${hit.id}`;
  }
}

/**
 * Universal search (TICKET FE-005).
 *
 * One box for a description, an SKU, an MPN, a scanned barcode, a location code
 * or a container code. Results are grouped, and an exact identifier match is
 * flagged — a resolved scan is a fact, a text match is a suggestion.
 */
export function GlobalSearch({ autoFocus = false }: { autoFocus?: boolean }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 180);
    return () => window.clearTimeout(timer);
  }, [term]);

  const { data, isFetching } = useSearch(debounced);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  // ⌘K / Ctrl+K focuses the box, the shortcut every tool of this kind has.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    return GROUPS.map((group) => ({ ...group, hits: data[group.key] })).filter(
      (group) => group.hits.length > 0,
    );
  }, [data]);

  function go(hit: SearchHit) {
    setOpen(false);
    setTerm("");
    navigate(hrefFor(hit));
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="sr-only" htmlFor="global-search">
        Recherche universelle
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
          aria-hidden
        />
        <input
          id="global-search"
          ref={inputRef}
          autoFocus={autoFocus}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && groups[0]?.hits[0]) go(groups[0].hits[0]);
          }}
          placeholder="Rechercher un produit, un outil, un emplacement…"
          className="h-10 w-full rounded-md border border-border bg-surface-sunken pl-9 pr-16 text-sm text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
        />
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-ink-subtle sm:block">
          ⌘K
        </kbd>
      </div>

      {open && debounced.length > 0 && (
        <div className="absolute inset-x-0 top-12 z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-surface-raised shadow-2xl">
          {isFetching && !data && (
            <p className="px-4 py-6 text-center text-[13px] text-ink-subtle">Recherche…</p>
          )}
          {data && data.total === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-[13px] text-ink-muted">Aucun résultat pour « {data.query} »</p>
              <p className="mt-1 text-[12px] text-ink-subtle">
                Un code inconnu ne crée rien automatiquement — c'est une décision humaine.
              </p>
            </div>
          )}
          {groups.map((group) => (
            <div key={group.key} className="border-b border-border last:border-b-0">
              <p className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                <group.icon className="size-3" aria-hidden />
                {group.label}
              </p>
              <ul>
                {group.hits.map((hit) => (
                  <li key={`${hit.kind}-${hit.id}`}>
                    <button
                      type="button"
                      onClick={() => go(hit)}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-surface-hover",
                      )}
                    >
                      {hit.kind === "product" ? (
                        <Photo mediaId={hit.media_id} alt={hit.title} className="size-9 shrink-0" />
                      ) : (
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken text-ink-subtle">
                          <group.icon className="size-4" aria-hidden />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] text-ink">{hit.title}</span>
                        <span className="block truncate text-[11px] text-ink-subtle">
                          {hit.subtitle}
                        </span>
                      </span>
                      {hit.exact && <StatusBadge tone="available">Exact</StatusBadge>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
